import { SearchQuery } from "../code/search-query/search-query";

export const MAX_PAGE_SIZE = 1000;

// ── Calendar events (company creates events for their employees) ───────────

export interface CalendarEventListItem {
  readonly uid: string;
  readonly title: string;
  readonly description?: string;
  readonly startDateTime: string;
  readonly endDateTime: string;
  readonly location?: string;
  readonly onlineLink?: string;
  readonly companyUid?: string;
  readonly companyName?: string;
}

export interface CalendarEventsListData
  extends Pagination<CalendarEventListItem> {}

export interface CalendarEventInput {
  readonly title: string;
  readonly description?: string;
  readonly startDateTime: string;
  readonly endDateTime: string;
  readonly location?: string;
  readonly onlineLink?: string;
  readonly companyUid?: string;
}

// ── Incidents (PD-02 §6.16.8 — managers see incidents from their company) ──
export enum IncidentSeverity {
  Low = "Low",
  Medium = "Medium",
  High = "High",
  Critical = "Critical",
}
export enum IncidentStatus {
  Open = "Open",
  InProgress = "InProgress",
  Resolved = "Resolved",
  Closed = "Closed",
}

export interface Incident {
  readonly uid: string;
  readonly rowVersion: string;
  readonly title: string;
  readonly description: string;
  readonly isAnonymous: boolean;
  readonly reportedDate: string;
  readonly status: IncidentStatus;
  readonly severity: IncidentSeverity;
  readonly reportedByUserUid?: string | null;
  readonly reportedByUserUserName?: string | null;
  readonly assignedToUserUid?: string | null;
  readonly assignedToUserUserName?: string | null;
  readonly incidentTypeUid?: string | null;
  readonly incidentTypeName?: string | null;
  readonly departmentUid?: string | null;
  readonly departmentName?: string | null;
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly createdOn?: string;
}

export interface IncidentsData extends Pagination<Incident> {}

// ── Loyalty history (company reads scoped by company) ──────────────────────

export interface LoyaltyHistoryEntry {
  readonly uid: string;
  readonly userUid: string;
  readonly userUserName?: string;
  readonly userFirstName?: string;
  readonly userLastName?: string;
  readonly pointsChange: number;
  readonly description?: string;
  readonly rewardName?: string;
  readonly createdOn?: string;
}

export interface LoyaltyHistoryData extends Pagination<LoyaltyHistoryEntry> {}

// ── Simple assignment inputs ───────────────────────────────────────────────

export interface AssignQuestionnaireInput {
  readonly userUid: string;
  readonly questionnaireUid: string;
  readonly companyUid?: string;
  readonly dueDate?: string;
}

// ── Rewards (Company creates company-scoped rewards) ───────────────────────

export interface Reward {
  readonly uid: string;
  readonly name: string;
  readonly description?: string;
  readonly requiredPoints: number;
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly isPublished?: boolean;
  readonly createdOn?: string;
}

export interface RewardsListData extends Pagination<Reward> {}

export interface RewardCreateInput {
  readonly name: string;
  readonly description?: string;
  readonly requiredPoints: number;
  readonly companyUid?: string;
}

export const ICON_SIZE = 70;

export interface PaginationInput {
  readonly searchQuery: SearchQuery;
  readonly pageSize: number;
  readonly pageNumber: number;
  readonly sortBy?: string | null;
  readonly sortDescending?: boolean | null;
}

export interface Pagination<T> {
  readonly data: T[];
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly totalRecords: number;
}

export interface UserHistory {
  readonly uid: string;
  readonly loginProvider: string;
  readonly timestamp: string;
  readonly ipAddress: string;
  readonly deviceInfo: string;
  readonly userUid: string;
  readonly userUserName: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly departmentUid: string;
  readonly departmentName: string;
  readonly vesselUid: string;
  readonly vesselName: string;
}

export interface UserHistoryData extends Pagination<UserHistory> {}

// Platform Users

export interface PlatformUser {
  readonly uid: string;
  readonly userName: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly middleName: string;
  readonly dateOfBirth: Date;
  readonly email: string;
  readonly phoneNumber: string;
  readonly userAvatar: string;
  readonly groupUid: string;
  readonly emailConfirmed: boolean;
}

export interface PlatformUserData extends Pagination<PlatformUser> {}

export interface AssignedEmployee {
  readonly uid: string;
  readonly userName: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly middleName: string;
  readonly dateOfBirth: Date;
  readonly email: string;
  readonly phoneNumber: string;
  readonly userAvatar: string;
  readonly groupUid: string;
  readonly emailConfirmed: boolean;
  readonly companyProfile: CompanyProfile;
}

export interface CompanyDomain {
  readonly name: string;
  readonly sysName: string;
  readonly description: string;
}

export interface CompanyProfile {
  readonly companyLicense: number;
  readonly logoBase64: string;
  readonly companyDomain: CompanyDomain;
}

// Roles

// export interface CompanyRole {
//   readonly id: number;
//   readonly uid: string;
//   readonly name: string;
//   readonly normalizedName: string;
//   readonly tenantUid: string;
//   readonly companyUid: string;
//   readonly rowVersion: string;
//   readonly assignedEmployees: AssignedEmployee[];
// }

export interface DepartmentInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly companyUid?: string;
}

export interface DepartmentData extends Pagination<Department> {}

export interface RegisterCompanyUserFileInput {
  readonly companyUid: string;
  readonly file: File;
}

export interface RegisterFilePreviewInput {
  readonly file: File;
  readonly companyUid?: string;
}

export interface RegisterCompanyUsersPreview {
  readonly companyUid?: string;
  readonly companyName?: string;
  readonly usersInfos: {
    readonly firstName: string;
    readonly email: string;
    readonly lastName: string;
    readonly dateOfBirth: string;
    readonly roleUid: string;
    readonly roleName: string;
    readonly departmentUid: string;
    readonly departmentName: string;
  }[];
}

export interface CompanyRole {
  readonly id: number;
  readonly uid: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly description: string;
  readonly isManager: boolean;
  readonly tenantUid: string;
  readonly tenantName: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly companyDomainUid: string;
  readonly companyDomainName: string;
  readonly usersCount: number;
  readonly createdOn: string;
  readonly rowVersion: string;
  readonly parentRoleUid: string;
  readonly parentRoleName: string;
  readonly childRolesCount: number;
}

export interface Department {
  readonly id: number;
  readonly uid: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly tenantUid: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly rowVersion: string;
  readonly assignedEmployees: AssignedEmployee[];
}

export interface CompanyRoleInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
}

// Organizational metrics (F-10) — manager-entered, per department, per fiscal quarter.

export type MetricType = "Absenteeism" | "Turnover" | "Grievance" | "Accident";

export interface OrganizationalMetricEntry {
  readonly uid: string;
  readonly companyUid?: string;
  readonly departmentUid: string;
  readonly departmentName?: string;
  readonly companyFiscalPeriodUid: string;
  readonly companyFiscalPeriodName?: string;
  readonly metricType: MetricType;
  readonly headcount: number;
  readonly scheduledWorkdays?: number | null;
  readonly eventCount: number;
  readonly voluntaryDepartures?: number | null;
  readonly involuntaryDepartures?: number | null;
  readonly retirements?: number | null;
  readonly rate: number;
  // Resolved server-side (the bands catalog is admin-only).
  readonly resultingBandLabel?: string | null;
  readonly resultingBandColor?: string | null;
  readonly readinessMultiplier?: number | null;
  readonly rowVersion?: string;
}

export interface OrganizationalMetricEntryData
  extends Pagination<OrganizationalMetricEntry> {}

export interface OrganizationalMetricInput {
  readonly uid?: string;
  readonly companyUid?: string | null;
  readonly departmentUid: string;
  readonly companyFiscalPeriodUid: string;
  readonly metricType: MetricType;
  readonly headcount: number;
  readonly scheduledWorkdays?: number | null;
  readonly eventCount: number;
  readonly voluntaryDepartures?: number | null;
  readonly involuntaryDepartures?: number | null;
  readonly retirements?: number | null;
  readonly rowVersion?: string;
}

export type FiscalPeriodType = "Year" | "Semester" | "Quarter";

export interface CompanyFiscalPeriod {
  readonly uid: string;
  readonly name: string;
  readonly periodType: FiscalPeriodType;
  readonly startDate?: string;
  readonly endDate?: string;
  readonly year?: number;
  readonly parentFiscalPeriodUid?: string | null;
  readonly parentName?: string | null;
  readonly companyUid?: string;
  readonly rowVersion?: string;
}

export interface CompanyFiscalPeriodData
  extends Pagination<CompanyFiscalPeriod> {}

export interface CompanyRoleData extends Pagination<CompanyRole> {}

// Policies

export interface CompanyPolicy {
  readonly name: string;
  readonly sysName: string;
  readonly description: string;
}

export interface CompanyPolicyData extends Pagination<CompanyPolicy> {}

// Vessels

export interface CompanyVessel {
  readonly id: number;
  readonly uid: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly code: string;
  readonly typeName: string;
  readonly typeUid: string;
  readonly tenantUid: string;
  readonly companyUid: string;
  readonly rowVersion: string;
  readonly assignedEmployees: AssignedEmployee[];
}

export interface CompanyVesselData extends Pagination<CompanyVessel> {}

export interface CompanyVesselInput {
  readonly uid?: string;
  readonly rowVersion?: string;
  readonly name: string;
  readonly code: string;
  readonly typeUid: string;
}

export interface CompanyVesselType {
  readonly uid: string;
  readonly rowVersion: string;
  readonly name: string;
}

export interface CompanyVesselTypeData extends Pagination<CompanyVesselType> {}

// Companies

export interface Company {
  readonly uid: string;
  readonly rowVersion: string;
  readonly name: string;
  readonly address: string;
  readonly email: string;
  readonly domain: string;
  readonly domainUid: string;
  readonly phoneNumber: string;
  readonly parentCompanyUid?: string;
  readonly parentCompanyName?: string;
}

export interface CompanyData extends Pagination<Company> {}

// Managers

export interface CompanyUser {
  readonly uid: string;
  readonly userName: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly middleName: string;
  readonly dateOfBirth: Date;
  readonly email: string;
  readonly phoneNumber: string;
  readonly userAvatar: string;
  readonly groupUid: string;
  readonly emailConfirmed: boolean;
  readonly companyUid?: string;
  readonly companyName?: string;
}

export interface CompanyUserData extends Pagination<CompanyUser> {}

// Employees

export interface EmployeeUser {
  readonly uid: string;
  readonly userName: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly middleName: string;
  readonly dateOfBirth: Date;
  readonly email: string;
  readonly phoneNumber: string;
  readonly userAvatar: string;
  readonly groupUid: string;
  readonly emailConfirmed: boolean;
  readonly companyUid?: string;
  readonly companyName?: string;
  // PD-02 §6.6.1 — employee grid required columns.
  readonly joinedDate?: string | null;
  readonly lastLoginAt?: string | null;
  readonly lockoutEnabled?: boolean;
  readonly lockoutEnd?: string | null;
}

export interface EmployeeUserData extends Pagination<EmployeeUser> {}

// Notifications

export interface MyNotification {
  readonly uid: string;
  readonly title: string;
  readonly message: string;
  readonly sentByUid: string;
  readonly sentByUserName: string;
  readonly sentAt: string;
  readonly deliveredAt?: string;
  readonly isRead: boolean;
  readonly readAt: string;
  readonly createdOn: string;
  readonly rowVersion: string;
}

export interface MyNotificationsData extends Pagination<MyNotification> {}

export interface MyNotificationRead {
  readonly uid?: string;
}

// Company Profile

export interface CompanyProfileInfo {
  readonly uid: string;
  readonly createdOn: string;
  readonly createdBy: string;
  readonly createdByUserUserName: string;
  readonly updatedOn: string;
  readonly updatedBy: string;
  readonly updatedByUserUserName: string;
  readonly isDeleted: boolean;
  readonly rowVersion: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly address: string;
  readonly email: string;
  readonly phoneNumber: string;
  readonly registrationNotes: string;
  readonly tenantUid: string;
  readonly tenantName: string;
  readonly domainUid: string;
  readonly domainName: string;
  readonly parentCompanyUid: string;
  readonly parentCompanyName: string;
  readonly departmentsCount: number;
  readonly subCompaniesCount: number;
  readonly usersCount: number;
  readonly vesselsCount: number;
}

export interface CompanyProfileInput {
  readonly uid: string;
  readonly rowVersion: string;
  readonly address?: string;
  readonly phoneNumber?: string;
  readonly email?: string;
}

// ── Processes (Roadmaps) — the admin-authored wellbeing journey, consumed read-only ──────────

// Mirrors the backend RoadmapLevelAction enum (single-select; Common/Definitions/PublicEnums.cs).
// The API serializes enums by NAME (JsonStringEnumConverter), so availableActions is the member name.
export const RoadmapLevelAction = {
  None: "None",
  AnswerQuestionnaire: "AnswerQuestionnaire",
  DistributeBundle: "DistributeBundle",
  CreatePolicy: "CreatePolicy",
  CreateProgram: "CreateProgram",
} as const;

export const RoadmapLevelActionLabels: Readonly<Record<string, string>> = {
  [RoadmapLevelAction.AnswerQuestionnaire]: "Answer Questionnaire",
  [RoadmapLevelAction.DistributeBundle]: "Distribute Bundle",
  [RoadmapLevelAction.CreatePolicy]: "Create Policy",
  [RoadmapLevelAction.CreateProgram]: "Create Program",
};

export interface Roadmap {
  readonly uid: string;
  readonly rowVersion: string;
  readonly name: string;
  readonly description: string;
  readonly shortDescription: string;
  readonly stepsDescription: string;
  readonly stagesCount: number;
  readonly companiesCount: number;
  readonly createdOn: string;
}

export interface RoadmapsData extends Pagination<Roadmap> {}

export interface RoadmapStage {
  readonly uid: string;
  readonly rowVersion: string;
  readonly name: string;
  readonly order: number;
  readonly description: string;
  readonly shortDescription: string;
  readonly stepsDescription: string;
  readonly roadmapUid: string;
  readonly roadmapName: string;
  readonly filesCount: number;
  readonly companyFilesCount: number;
}

export interface RoadmapStagesData extends Pagination<RoadmapStage> {}

export interface RoadmapRequirement {
  readonly uid: string;
  readonly rowVersion: string;
  readonly name: string;
  readonly order: number;
  readonly description: string;
  readonly shortDescription: string;
  readonly stepsDescription: string;
  readonly roadmapLevelUid: string;
  readonly roadmapLevelName: string;
  readonly availableActions: string | null;
  readonly assignableBundleUid?: string | null;
  readonly assignableBundleName?: string | null;
  readonly assignableQuestionnaireUid?: string | null;
  readonly assignableQuestionnaireName?: string | null;
  /** The step's questionnaire across its versions (the step points at the current published one). */
  readonly assignableQuestionnaireLineageUid?: string | null;
}

export interface RoadmapRequirementsData extends Pagination<RoadmapRequirement> {}

// The caller's company's progress (from the self-scoped MyRoadmapProgress endpoint).
export interface RoadmapCompany {
  readonly uid: string;
  readonly rowVersion: string;
  readonly roadmapUid: string;
  readonly roadmapName: string;
  readonly companyUid: string;
  readonly companyName: string;
  readonly currentStage: number;
  readonly currentRequirement: number;
  readonly companyFilesCount: number;
}

// ── Questionnaires + answers (manager "answer on the spot", draft-first) ─────────────────────

export interface Questionnaire {
  readonly uid: string;
  readonly rowVersion: string;
  readonly name: string;
  readonly type: string;
  readonly description: string;
  readonly duration: number;
  readonly sectionsCount: number;
}

export interface QuestionnaireSection {
  readonly uid: string;
  readonly name: string;
  readonly description: string;
  readonly questionsCount: number;
  readonly questionnaireUid: string;
  readonly order: number;
  readonly rowVersion?: string;
}

export interface QuestionnaireSectionsData extends Pagination<QuestionnaireSection> {}

export interface QuestionnaireQuestion {
  readonly uid: string;
  readonly description: string;
  readonly questionType: "FreeText" | "SingleChoice" | "MultipleChoice" | "Numeric";
  readonly minValue?: number;
  readonly maxValue?: number;
  readonly unit?: string;
  readonly isRequired: boolean;
  /**
   * Feeds a banded (assessment) KPI — the server will not complete the questionnaire while such a question is
   * unanswered. Data-collection questions (count KPIs only) are not assessed: an empty answer is valid.
   */
  readonly isAssessed?: boolean;
  readonly order: number;
  readonly sectionUid: string;
  readonly choicesCount: number;
  readonly rowVersion?: string;
}

export interface QuestionnaireQuestionsData extends Pagination<QuestionnaireQuestion> {}

export interface QuestionnaireQuestionChoice {
  readonly uid: string;
  readonly description: string;
  readonly order: number;
  readonly weight: number;
  readonly questionUid: string;
  readonly rowVersion?: string;
}

export interface QuestionnaireQuestionChoicesData
  extends Pagination<QuestionnaireQuestionChoice> {}

// A questionnaire answer header (draft while !completed).
export interface QuestionnaireAnswer {
  readonly uid: string;
  readonly rowVersion: string;
  readonly userUid: string;
  readonly questionnaireUid: string;
  readonly questionnaireName: string;
  /** The questionnaire across its versions, and the version this answer was given on. */
  readonly questionnaireLineageUid?: string;
  readonly questionnaireVersion?: number;
  readonly companyUid?: string | null;
  readonly completed: boolean;
  readonly questionAnswersCount: number;
  readonly createdOn?: string;
  readonly updatedOn?: string | null;
}

export interface QuestionnaireAnswersData extends Pagination<QuestionnaireAnswer> {}

export interface QuestionnaireQuestionAnswer {
  readonly uid: string;
  readonly rowVersion: string;
  readonly questionnaireAnswerUid: string;
  readonly questionUid: string;
  readonly freeTextAnswer?: string | null;
  readonly numericAnswer?: number | null;
  readonly selectedChoices: string[];
  readonly createdOn?: string;
  readonly updatedOn?: string | null;
}

export interface QuestionnaireQuestionAnswersData
  extends Pagination<QuestionnaireQuestionAnswer> {}

// Inputs
export interface QuestionnaireAnswerInput {
  readonly userUid: string;
  readonly questionnaireUid: string;
  readonly bundleUid?: string | null;
  readonly companyUid?: string | null;
}

export interface QuestionnaireAnswerCompleteInput {
  readonly uid: string;
  readonly rowVersion: string;
  readonly completed: boolean;
}

export interface QuestionnaireQuestionAnswerInput {
  readonly questionnaireAnswerUid: string;
  readonly questionUid: string;
  readonly freeTextAnswer?: string | null;
  readonly numericAnswer?: number | null;
  readonly selectedChoices: string[];
}

export interface QuestionnaireQuestionAnswerUpdateInput {
  readonly uid: string;
  readonly rowVersion: string;
  readonly freeTextAnswer?: string | null;
  readonly numericAnswer?: number | null;
  readonly selectedChoices: string[];
}
